import { defineStore } from 'pinia'
import { apiErrorMessage } from '@/api'
import { ref } from 'vue'
import { dashboardApi } from '@/api/dashboard'
import type {
  DashboardAccountHealth,
  DashboardCampaignFunnel,
  DashboardNeedsAttention,
  DashboardView,
} from '@/types/models'

/**
 * The workspace dashboard (PRD Slice 12): "what's running, what's working,
 * what needs a human". Every figure here is exactly what the API computed
 * (no shadow math client-side) — this store only holds and refreshes it.
 */
export const useDashboardStore = defineStore('dashboard', () => {
  const view = ref<DashboardView>('workspace')
  const campaignFunnels = ref<DashboardCampaignFunnel[]>([])
  const accountHealth = ref<DashboardAccountHealth[]>([])
  const needsAttention = ref<DashboardNeedsAttention | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const snoozingAccountId = ref<string | null>(null)
  const snoozeError = ref<string | null>(null)

  async function fetchDashboard(workspaceId: string) {
    loading.value = true
    error.value = null
    try {
      const dashboard = await dashboardApi.show(workspaceId, view.value)
      campaignFunnels.value = dashboard.campaign_funnels
      accountHealth.value = dashboard.account_health
      needsAttention.value = dashboard.needs_attention
    } catch (e: unknown) {
      error.value = apiErrorMessage(e, 'Failed to load the dashboard.')
    } finally {
      loading.value = false
    }
  }

  function setView(next: DashboardView) {
    view.value = next
  }

  async function snoozeExportFreshness(workspaceId: string, linkedinAccountId: string) {
    snoozingAccountId.value = linkedinAccountId
    snoozeError.value = null
    try {
      const updated = await dashboardApi.snoozeExportFreshness(workspaceId, linkedinAccountId)
      const index = accountHealth.value.findIndex(
        (entry) => entry.linkedin_account_id === linkedinAccountId,
      )
      if (index !== -1) {
        accountHealth.value[index] = {
          ...accountHealth.value[index],
          last_successful_import_at: updated.last_successful_import_at,
          export_freshness_nudge_due: updated.export_freshness_nudge_due,
        }
      }
    } catch (e: unknown) {
      snoozeError.value = apiErrorMessage(e, 'Failed to snooze this reminder.')
      throw e
    } finally {
      snoozingAccountId.value = null
    }
  }

  return {
    view,
    campaignFunnels,
    accountHealth,
    needsAttention,
    loading,
    error,
    snoozingAccountId,
    snoozeError,
    fetchDashboard,
    setView,
    snoozeExportFreshness,
  }
})
